<template>
  <article>
    <activity-stage
      :customRootContainerClass="`type-to-see ${keyboardEnabled ? '' : 'full-height'}`"
      :customContentContainerStyles="{ height: 'calc(100% - 50px)' }"
    >
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article
        slot="content"
        :class="{ isMobileView: isMobile }"
        id="sub-content-container"
        style="height: 100%; position: relative;"
      >
        <div class="top-to-bottom-reveal-container block-container">
          <div
            v-for="(block, index) of groupedBlocks"
            class="block"
            :id="`block-${index}`"
            :key="index"
            :style="{
              height: `${100 / groupedBlocks.length}%`,
              'min-height': index === groupedBlocks.length - 1 ? 'auto' : '90px'
            }"
          >
            <div class="block-paragraph" v-for="(step, stepIndex) of block" :key="step.id">
              <blinking-cursor-text
                :text="currentRound.steps.find(({ id }) => id === step.id).text"
                :text-step="currentRound.steps.findIndex(({ id }) => id === step.id)"
              >
              </blinking-cursor-text>
            </div>
          </div>
        </div>
        <div id="img-overlay">
          <div id="img-container">
            <img alt="Main image" id="img" :src="getImageSrc()" />
          </div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { groupBy } from 'lodash';
import { TweenMax, Back } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';

import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import ActivityStage from 'components/activity/ActivityStage.vue';

export default {
  mixins: [AssetLoader, ActivityManager, TypingActivityHelper, ErrorLogger],

  components: {
    ActivityStage,
    BlinkingCursorText
  },

  data() {
    return {
      activityName: 'TopBottomPanelReveal',
      groupedBlocks: []
    };
  },

  mounted() {
    window.addEventListener('resize', this.resizeImageOverlay);
  },

  destroyed() {
    window.removeEventListener('resize', this.resizeImageOverlay);
  },

  methods: {
    getImageSrc() {
      const src = this.currentRound.contentImage?.image.src || '';

      return src;
    },

    async loadActivity() {
      const activityState = this.getActivityState();
      this.createRounds(activityState);
      await this.loadImages();
    },

    stepCallback(rootGetters) {
      return rootGetters['activity/currentStep']['text'];
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds,
        instructions: {
          voice: `${this.voiceInstructionsPrefix}/typetosee-fa-1.mp3`,
          written: 'Type the lines of information.'
        }
      };
    },

    fadeInTypeToSee() {
      const elem = document.querySelector('.top-to-bottom-reveal-container');
      TweenMax.to(elem, 1, { css: { opacity: 1 } });
    },

    getKeyboardConfig() {
      const { keyboardType } = this.activityData.keyboard;

      return {
        keyboardType,
        keys: undefined,
        hideNumberRow: false
      };
    },

    async loadImages() {
      this.rounds.forEach(({ contentImage }) => {
        this.queueImage(contentImage.image.src);
      });

      return this.downloadImages();
    },

    setImageHeight() {
      const contentContainerEl = document.querySelector('.content-container');
      const blockEls = document.getElementsByClassName('block');
      const imgEl = document.getElementById('img');

      if (!contentContainerEl || !blockEls || !imgEl) return;

      const contentContainerHeight = contentContainerEl.clientHeight;
      let blocksHeight = 0;

      Array.from(blockEls).forEach(block => {
        blocksHeight += block.clientHeight;
      });

      const imgHeight =
        contentContainerHeight >= blocksHeight ? contentContainerHeight : blocksHeight;

      imgEl.style.height = `${imgHeight}px`;
      imgEl.style.width = 'auto';
    },

    startActivity() {
      this.setupNewRound({ stepCallback: this.stepCallback });
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(() => {
        this.setActivityContainerHeight();
      });
      this.$nextTick(this.fadeInTypeToSee);
      this.$nextTick(this.setImageHeight);
    },

    getCurrentBlock() {
      return this.currentRound.steps[this.progress.stepIndex];
    },

    getNextBlock() {
      return this.currentRound.steps[this.progress.stepIndex + 1];
    },

    isLastStepInBlock() {
      const allBlocks = this.currentRound.steps.filter(
        step => step.blockId === this.currentStep.blockId
      );
      return allBlocks[allBlocks.length - 1].id === this.currentStep.id;
    },

    getImageOverlayHeight(nextBlock = null) {
      const currentElem = document.getElementById(`block-${this.currentStep.blockId}`);
      const nextElem = nextBlock
        ? document.getElementById(`block-${nextBlock.blockId}`)
        : undefined;

      const posY = (nextElem ? nextElem.offsetTop : currentElem.offsetTop) - 5;

      return posY;
    },

    setImageOverlayHeight(nextBlock = null, isRoundComplete = false) {
      const imgEl = document.getElementById('img');
      const nextOverlayHeight = isRoundComplete
        ? imgEl.clientHeight
        : this.getImageOverlayHeight(nextBlock);

      TweenMax.to('#img-overlay', 2, { height: nextOverlayHeight, ease: Back.easeInOut });
    },

    handleStepEnd() {
      this.sndBell();
      this.completeStep();

      if (this.isLastStepInBlock()) {
        const nextBlock = this.getNextBlock();
        TweenMax.to(`#block-${this.currentStep.blockId}`, 0.5, { css: { opacity: 0 } });
        this.setImageOverlayHeight(nextBlock, this.isLastStepInRound);
      }

      if (this.isEndOfActivity) {
        TweenMax.delayedCall(2, () => {
          this.hideKeyboard();
          this.showImgOverlay();
        });
        TweenMax.delayedCall(2, this.playCongrats);
        TweenMax.delayedCall(4, () => this.completeActivity(false));
      } else {
        this.setCurrentStep({ stepCallback: this.stepCallback });
      }
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.$nextTick(() => this.handleStepEnd());
      }
    },

    showImgOverlay() {
      const typeToSee = document.querySelector('.type-to-see');

      const subContentContainerElEl = document.getElementById('sub-content-container');
      const blockContainerEl = document.querySelector('.block-container');
      const imgOverlayEl = document.getElementById('img-overlay');
      const imgEl = document.getElementById('img');

      if (subContentContainerElEl && blockContainerEl && imgOverlayEl && imgEl) {
        subContentContainerElEl.style.height = typeToSee.style.height;
        blockContainerEl.style.display = 'none';
        imgOverlayEl.style.border = 'none';
        imgOverlayEl.style.height = typeToSee.style.height;
        imgEl.style.height = typeToSee.style.height;
      }
    },

    resizeImageOverlay() {
      if (this.keyboardEnabled) {
        const currentBlock = this.getCurrentBlock();
        this.setImageOverlayHeight(currentBlock);
        this.setImageHeight();
      } else {
        this.showImgOverlay();
      }
    }
  },

  watch: {
    currentRound(next) {
      if (Object.keys(next).length) {
        const groupedSteps = groupBy(this.currentRound.steps, 'blockId');
        this.groupedBlocks = Object.values(groupedSteps);
      }
    },
    expectedKey() {
      const { completedSteps, stepIndex } = this.progress;

      const phrase = this.currentRound.steps?.[stepIndex]?.text;

      if (!phrase) return;

      const instructions = `Type the phrase - ${phrase}`;

      if (instructions === this.currentScreenReaderAlert) {
        return;
      }

      this.rerenderScreenReaderAlert(instructions);
    }
  }
};
</script>

<style lang="scss" scoped>
.type-to-see {
  .content-container {
    height: 100% !important;
  }
}
.top-to-bottom-reveal-container {
  opacity: 0;
  // padding-top: 5px;
}

.block-spacer {
  margin-top: 70px;
}

.mobile-block-spacer {
  margin-top: 10px;
}

.block-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-left: 20px;
}

.block-paragraph p {
  font-size: 18px;
}

.block {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  &:first-child {
    margin-top: 5px;
  }
}

.activity-stage {
  height: 768px;
  position: relative;
}

.activity-stage.isMobileView {
  height: 100vh;
}

#img-overlay {
  border-bottom: 4px solid #000;
  height: 0;
  left: 0;
  overflow: auto hidden;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 100;
}

#img-container {
  height: 100%;
  width: 100%;
  text-align: center;

  img {
    max-height: 382px;
    width: auto;
  }
}
</style>
